import {
  collection, doc, addDoc, updateDoc, deleteDoc, setDoc,
  query, where, orderBy, getDocs, getDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

export type SectionType =
  | "intro" | "verse" | "pre-chorus" | "chorus" | "post-chorus"
  | "bridge" | "breakdown" | "outro" | "hook" | "interlude" | "custom";

export const SECTION_LABELS: Record<SectionType, string> = {
  intro: "Intro",
  verse: "Verse",
  "pre-chorus": "Pre-Chorus",
  chorus: "Chorus",
  "post-chorus": "Post-Chorus",
  bridge: "Bridge",
  breakdown: "Breakdown",
  outro: "Outro",
  hook: "Hook",
  interlude: "Interlude",
  custom: "Section",
};

export interface SongSection {
  id: string;
  type: SectionType;
  label?: string; // custom label (e.g. "Verse 2")
  lyrics: string;
  chords: string; // e.g. "Am G F E"
  melody: string; // free text: solfège, ABC notation, pitch sequence
  attachedMemoIds: string[];
  order: number;
}

export interface Song {
  id: string;
  uid: string;
  title: string;
  bpm: number | null;
  key: string; // "C", "Am", etc.
  timeSignature: string; // "4/4", "3/4", etc.
  genre: string;
  notes: string; // overall song notes
  sections: SongSection[];
  status: "draft" | "in-progress" | "complete";
  collaborators: string[]; // uid list
  createdAt: Date;
  updatedAt: Date;
}

const COLLECTION = "songs";

function makeSectionId(): string {
  return `sec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function makeDefaultSection(type: SectionType = "verse", order = 0): SongSection {
  return {
    id: makeSectionId(),
    type,
    label: "",
    lyrics: "",
    chords: "",
    melody: "",
    attachedMemoIds: [],
    order,
  };
}

/** `init`: a note to start from — coπ's "song idea: …" (notes) or "new lyrics: …" (first verse). */
export async function createSong(uid: string, title = "Untitled Song", init?: { notes?: string; lyrics?: string }): Promise<Song> {
  const sections: SongSection[] = [
    makeDefaultSection("verse", 0),
    makeDefaultSection("chorus", 1),
  ];
  if (init?.lyrics) sections[0].lyrics = init.lyrics;
  const notes = init?.notes || "";
  const docRef = await addDoc(collection(db, COLLECTION), {
    uid,
    title,
    bpm: null,
    key: "",
    timeSignature: "4/4",
    genre: "",
    notes,
    sections,
    status: "draft",
    collaborators: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return {
    id: docRef.id,
    uid,
    title,
    bpm: null,
    key: "",
    timeSignature: "4/4",
    genre: "",
    notes,
    sections,
    status: "draft",
    collaborators: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export async function getUserSongs(uid: string): Promise<Song[]> {
  const q = query(
    collection(db, COLLECTION),
    where("uid", "==", uid),
    orderBy("updatedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => parseSongDoc(d.id, d.data()));
}

export async function getSong(id: string): Promise<Song | null> {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return parseSongDoc(snap.id, snap.data());
}

export async function saveSong(id: string, updates: Partial<Omit<Song, "id" | "uid" | "createdAt">>): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSong(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

function parseSongDoc(id: string, data: Record<string, any>): Song {
  return {
    id,
    uid: data.uid,
    title: data.title || "Untitled Song",
    bpm: data.bpm ?? null,
    key: data.key || "",
    timeSignature: data.timeSignature || "4/4",
    genre: data.genre || "",
    notes: data.notes || "",
    sections: (data.sections || []).sort((a: SongSection, b: SongSection) => a.order - b.order),
    status: data.status || "draft",
    collaborators: data.collaborators || [],
    createdAt: (data.createdAt as Timestamp)?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as Timestamp)?.toDate?.() || new Date(),
  };
}
